// ============================================================
// DockerCICD.jsx
// ============================================================
// Junior-dev Notes:
// - Concept: "Build once, run anywhere."
// - Key Term: "Immutable Infrastructure" (Don't patch, replace).
// - Strategy: Visualize the automation pipeline from GitHub to Cloud.
// - Branding: prodevconsult.com
// ============================================================

import React from "react";
import {
    Box,
    Container,
    Typography,
    Grid,
    Button,
    Stack,
    styled,
    Divider,
    Paper,
    Chip,
} from "@mui/material";
import { Link } from "react-router-dom";

/* MUI Icons */
import GitHubIcon from "@mui/icons-material/GitHub";
import SpeedIcon from "@mui/icons-material/Speed";
import PrecisionManufacturingIcon from "@mui/icons-material/PrecisionManufacturing";
import CloudDoneIcon from "@mui/icons-material/CloudDone";
import IntegrationInstructionsIcon from "@mui/icons-material/IntegrationInstructions";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

/* ── Brand tokens ────────────────────────────────────────── */
const DOCKER_BLUE = "#E5C95F"; // Gold
const NAVY_BG = "#100720";   // Neural Purple
const TEXT_WHITE = "#FFFFFF";

const BRAND = {
    muted: "rgba(255,255,255,0.7)",
    surface: "rgba(255,255,255,0.03)",
    border: "rgba(229, 201, 95, 0.2)",
    domain: "prodevconsult.com"
};

/* ── Styled Components ───────────────────────────────────── */

const PageWrapper = styled(Box)(({ theme }) => ({
    minHeight: "100vh",
    backgroundColor: NAVY_BG,
    backgroundImage: `radial-gradient(circle at 2px 2px, rgba(255,255,255,0.05) 1px, transparent 0)`,
    backgroundSize: "40px 40px",
    color: TEXT_WHITE,
    paddingTop: theme.spacing(12),
    paddingBottom: theme.spacing(10),
}));

const PipelineStep = styled(Paper)(({ theme, active }) => ({
    padding: theme.spacing(3),
    backgroundColor: active ? "rgba(229, 201, 95, 0.05)" : "rgba(255,255,255,0.02)",
    border: `1px solid ${active ? DOCKER_BLUE : BRAND.border}`,
    borderRadius: "12px",
    textAlign: "center",
    position: "relative",
    zIndex: 2,
    "&:hover": {
        transform: "scale(1.05)",
        borderColor: DOCKER_BLUE
    }
}));

const Connector = styled(Box)({
    height: "2px",
    backgroundColor: "rgba(229, 201, 95, 0.3)",
    flexGrow: 1,
    marginTop: "24px",
    position: "relative",
    "&::after": {
        content: '""',
        position: "absolute",
        right: 0,
        top: "-4px",
        width: "0",
        height: "0",
        borderTop: "5px solid transparent",
        borderBottom: "5px solid transparent",
        borderLeft: `8px solid ${DOCKER_BLUE}`,
    }
});

export default function DockerCICD() {
    return (
        <PageWrapper>
            <Container maxWidth="lg">

                {/* 1. HERO HEADER */}
                <Box sx={{ mb: 8, textAlign: { xs: "center", md: "left" } }}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2, justifyContent: { xs: "center", md: "flex-start" } }}>
                        <Chip
                            label="Automation · Module 15"
                            sx={{ bgcolor: "rgba(229, 201, 95, 0.1)", color: DOCKER_BLUE, fontWeight: 700 }}
                        />
                        <Typography variant="caption" sx={{ color: DOCKER_BLUE, fontWeight: 800, letterSpacing: 1 }}>
                            {BRAND.domain.toUpperCase()}
                        </Typography>
                    </Stack>

                    <Typography variant="h2" sx={{ fontWeight: 900, mb: 2, letterSpacing: "-1.5px" }}>
                        Docker in <span style={{ color: DOCKER_BLUE }}>CI/CD Pipelines</span>
                    </Typography>
                    <Typography variant="h6" sx={{ color: BRAND.muted, maxWidth: 800, lineHeight: 1.6 }}>
                        CI/CD is the heartbeat of DevOps. Docker ensures that what you build
                        on your laptop is exactly what gets tested, and exactly what gets
                        deployed to the cloud.
                    </Typography>
                </Box>

                {/* 2. THE PIPELINE VISUALIZATION */}
                <Typography variant="h5" sx={{ mb: 6, fontWeight: 800, textAlign: 'center' }}>
                    The <span style={{ color: DOCKER_BLUE }}>Automated</span> Flow
                </Typography>



                <Stack direction={{ xs: "column", md: "row" }} alignItems="flex-start" spacing={0} sx={{ mb: 10, px: 2 }}>
                    <PipelineStep sx={{ width: { xs: "100%", md: "200px" } }}>
                        <GitHubIcon sx={{ color: DOCKER_BLUE, mb: 1 }} />
                        <Typography variant="subtitle2" fontWeight={800}>1. COMMIT</Typography>
                        <Typography variant="caption" color={BRAND.muted}>Developer pushes code to GitHub.</Typography>
                    </PipelineStep>

                    <Connector sx={{ display: { xs: "none", md: "block" } }} />

                    <PipelineStep active sx={{ width: { xs: "100%", md: "200px" } }}>
                        <PrecisionManufacturingIcon sx={{ color: DOCKER_BLUE, mb: 1 }} />
                        <Typography variant="subtitle2" fontWeight={800}>2. BUILD</Typography>
                        <Typography variant="caption" color={BRAND.muted}>CI Server builds the Docker Image.</Typography>
                    </PipelineStep>

                    <Connector sx={{ display: { xs: "none", md: "block" } }} />

                    <PipelineStep sx={{ width: { xs: "100%", md: "200px" } }}>
                        <IntegrationInstructionsIcon sx={{ color: DOCKER_BLUE, mb: 1 }} />
                        <Typography variant="subtitle2" fontWeight={800}>3. TEST</Typography>
                        <Typography variant="caption" color={BRAND.muted}>Automated tests run inside the container.</Typography>
                    </PipelineStep>

                    <Connector sx={{ display: { xs: "none", md: "block" } }} />

                    <PipelineStep sx={{ width: { xs: "100%", md: "200px" } }}>
                        <CloudDoneIcon sx={{ color: "#00ED64", mb: 1 }} />
                        <Typography variant="subtitle2" fontWeight={800}>4. DEPLOY</Typography>
                        <Typography variant="caption" color={BRAND.muted}>Image is pushed to Registry & Production.</Typography>
                    </PipelineStep>
                </Stack>

                {/* 3. WHY DOCKER FOR CI/CD? */}
                <Grid container spacing={3} sx={{ mb: 10 }}>
                    {[
                        {
                            title: "Environment Parity",
                            icon: <SpeedIcon />,
                            desc: "Eliminates 'It works on my machine'. If it runs in the container on your laptop, it runs in the pipeline."
                        },
                        {
                            title: "Isolated Testing",
                            icon: <PrecisionManufacturingIcon />,
                            desc: "Every test run gets a clean, fresh environment. No leftover database state from previous runs."
                        },
                        {
                            title: "Faster Onboarding",
                            icon: <IntegrationInstructionsIcon />,
                            desc: "New developers can start contributing immediately by simply running 'docker-compose up' to replicate the stack."
                        }
                    ].map((item, i) => (
                        <Grid item xs={12} md={4} key={i}>
                            <Paper sx={{ p: 4, bgcolor: "rgba(255,255,255,0.02)", border: `1px solid ${BRAND.border}`, borderRadius: 4, height: '100%' }}>
                                <Box sx={{ color: DOCKER_BLUE, mb: 2 }}>{item.icon}</Box>
                                <Typography variant="h6" fontWeight={800} gutterBottom>{item.title}</Typography>
                                <Typography variant="body2" sx={{ color: BRAND.muted, lineHeight: 1.7 }}>{item.desc}</Typography>
                            </Paper>
                        </Grid>
                    ))}
                </Grid>

                {/* 4. PRO TIP: GITHUB ACTIONS */}
                <Paper sx={{
                    p: 4,
                    bgcolor: "rgba(229, 201, 95, 0.05)",
                    borderLeft: `5px solid ${DOCKER_BLUE}`,
                    borderRadius: "0 16px 16px 0"
                }}>
                    <Typography variant="h6" fontWeight={800} gutterBottom sx={{ color: DOCKER_BLUE }}>
                        💡 Practical Mastery: GitHub Actions
                    </Typography>
                    <Typography variant="body2" sx={{ color: BRAND.muted, mb: 2 }}>
                        Most modern teams use <strong>GitHub Actions</strong> to automate this. Your workflow file tells GitHub:
                        "Every time I push to main, build this Dockerfile, and if it passes tests, push it to Docker Hub."
                    </Typography>
                    <Box sx={{ p: 2, bgcolor: "#011627", borderRadius: 2, border: "1px solid rgba(173, 219, 103, 0.2)" }}>
                        <Typography sx={{ fontFamily: 'monospace', color: "#ADDB67", fontSize: '0.85rem' }}>
                            - name: Build and push Docker image <br/>
                            &nbsp;&nbsp;uses: docker/build-push-action@v2
                        </Typography>
                    </Box>
                </Paper>

                <Divider sx={{ bgcolor: "rgba(255,255,255,0.05)", my: 8 }} />

                {/* 5. FOOTER */}
                <Box sx={{ textAlign: "center", py: 4 }}>
                    <Typography variant="h5" sx={{ mb: 4, fontWeight: 700 }}>Infrastructure Automated. Let's head to Orchestration.</Typography>
                    <Link to="/docker-orchestration" style={{ textDecoration: "none" }}>
                        <Button
                            variant="contained"
                            size="large"
                            endIcon={<ArrowForwardIcon />}
                            sx={{
                                bgcolor: DOCKER_BLUE,
                                color: NAVY_BG,
                                fontWeight: 800,
                                px: 8,
                                py: 2,
                                borderRadius: "4px",
                                boxShadow: `0 4px 14px rgba(229, 201, 95, 0.3)`,
                                "&:hover": { bgcolor: "#d4b950" }
                            }}
                        >
                            Next: Container Orchestration
                        </Button>
                    </Link>
                    <Typography variant="caption" display="block" sx={{ mt: 4, opacity: 0.3 }}>
                        Validated Technical Training by {BRAND.domain}
                    </Typography>
                </Box>

            </Container>
        </PageWrapper>
    );
}
